import type {
  AccountSummary,
  Airport,
  Flight,
  FlightApiStatus,
  GameState,
  GoalType,
  PrivacyContact,
  Round,
  UserProfile,
} from '@flugwettrennen/shared';

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]!) : null;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    const csrf = readCookie('csrf');
    if (csrf) headers['x-csrf-token'] = csrf;
  }
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError('network', 'Netzwerkfehler', 0);
  }

  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string } }).error;
    throw new ApiError(err?.code ?? 'generic', err?.message ?? 'Fehler', res.status);
  }
  return data as T;
}

const get = <T>(p: string) => request<T>('GET', p);
const post = <T>(p: string, body?: unknown) => request<T>('POST', p, body ?? {});
const patch = <T>(p: string, body: unknown) => request<T>('PATCH', p, body);
const del = <T>(p: string, body?: unknown) => request<T>('DELETE', p, body);

export interface LeaderboardEntry {
  roundId: string;
  ownerId: string;
  ownerName: string;
  status: 'active' | 'finished' | 'abandoned';
  totalDurationMinutes: number | null;
  flights: number;
}

export interface CreateRoundBody {
  name: string;
  goalTypes: GoalType[];
  startAirportIata: string;
  destAirportIata: string;
  startDateTime: string;
  mode: 'live' | 'sim';
  options?: Record<string, unknown>;
}

export interface UpdateRoundBody {
  name?: string;
  options?: Record<string, unknown>;
}

export const api = {
  auth: {
    me: () =>
      get<{ user: UserProfile | null; inviteOnly: boolean; privacyContact: PrivacyContact }>(
        '/auth/me',
      ),
    login: (email: string, password: string) =>
      post<{ user: UserProfile }>('/auth/login', { email, password }),
    register: (body: {
      email: string;
      password: string;
      displayName: string;
      inviteCode?: string;
      language?: 'de' | 'en';
    }) => post<{ user: UserProfile }>('/auth/register', body),
    logout: () => post<{ ok: true }>('/auth/logout'),
    deleteAccount: (password: string) => del<{ ok: true }>('/auth/me', { password }),
    setLanguage: (language: 'de' | 'en') => post<{ ok: true }>('/auth/language', { language }),
  },
  airports: {
    search: (q: string) => get<{ airports: Airport[] }>(`/airports/search?q=${encodeURIComponent(q)}`),
    get: (iata: string) => get<{ airport: Airport }>(`/airports/${iata}`),
  },
  rounds: {
    list: () => get<{ rounds: Round[] }>('/rounds'),
    create: (body: CreateRoundBody) => post<{ round: Round; state: GameState }>('/rounds', body),
    get: (id: string) =>
      get<{ round: Round; state: GameState; selectedFlight: Flight | null }>(`/rounds/${id}`),
    join: (code: string) => post<{ round: Round; state: GameState }>('/rounds/join', { code }),
    update: (id: string, body: UpdateRoundBody) =>
      patch<{ round: Round; state: GameState }>(`/rounds/${id}`, body),
    reset: (id: string) => post<{ round: Round; state: GameState }>(`/rounds/${id}/reset`),
    remove: (id: string) => del<void>(`/rounds/${id}`),
    challenge: (code: string) =>
      get<{ code: string; goalTypes: GoalType[]; name: string; entries: LeaderboardEntry[] }>(
        `/rounds/challenge/${encodeURIComponent(code)}`,
      ),
  },
  game: {
    /**
     * Abflugtafel. `windows` sind Zwölf-Stunden-Fenster ab der Spieluhr –
     * jedes zusätzliche kostet eine Anfrage beim Datenanbieter.
     */
    departures: (id: string, windows = 1) =>
      get<{ flights: Flight[]; windows: number; maxWindows: number }>(
        `/game/${id}/departures?windows=${windows}`,
      ),
    roll: (id: string, flightId: string) =>
      post<{ allowed: boolean; state: GameState }>(`/game/${id}/roll`, { flightId }),
    change: (id: string) => post<{ state: GameState }>(`/game/${id}/change`),
    advance: (id: string, auto = false) =>
      post<{ state: GameState; waitingUntil: string | null; selectedFlight: Flight | null }>(
        `/game/${id}/advance`,
        { auto },
      ),
    finish: (id: string) => post<{ round: Round }>(`/game/${id}/finish`),
    live: (id: string) =>
      get<{
        flight: Flight;
        position: {
          lat: number;
          lon: number;
          altitudeFt: number | null;
          groundspeedKt: number | null;
          airborne: boolean;
        } | null;
        /** false = der Server fragt die Position nicht ab; Nachfragen lohnt nicht. */
        positionTracking: boolean;
      }>(`/game/${id}/live`),
  },
  settings: {
    flightApi: () => get<{ status: FlightApiStatus }>('/settings/flight-api'),
  },
  admin: {
    createInvite: () => post<{ code: string }>('/admin/invites'),
    listInvites: () => get<{ invites: { code: string; usedBy: string | null; createdAt: string }[] }>('/admin/invites'),
    listUsers: () => get<{ users: AccountSummary[] }>('/admin/users'),
    deleteUser: (id: string) => del<{ ok: true }>(`/admin/users/${encodeURIComponent(id)}`),
  },
};
